import {
  Button,
  CollapseButton,
  DefaultAlertDialog,
  DefaultModalDialog,
  DefaultScreenTileBox,
  InputField,
  LabeledText,
  SelectField,
  Text,
} from '~/common/components/defaultUIComponents';
import {
  Collapse,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import {
  Department,
  Employee,
} from '~/common/entities/Department/DepartmentTypes';
import { useDepartmentService } from '~/common/services/DepartmentService';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import EmployeesInTile from '~/common/components/EmployeesInTile';
import { useDepartmentContext } from './departmentContext';
import React, { useEffect, useRef, useState } from 'react';
import { formatName } from '~/common/utils/employeeUtils';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { Form, Formik, FormikHelpers } from 'formik';
import useTranslate from '~/common/utils/translate';
import _ from 'lodash';

type DepartmentInput = {
  department: Department;
};

const DepartmentTile: React.FC<DepartmentInput> = ({
  department,
}: DepartmentInput) => {
  const { openDepartment, setOpenDepartment } = useDepartmentContext();
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const departmentService = useDepartmentService();
  const [employeeQueryResult] = EmployeeApi.hooks.useEmployeeQuery();
  const allEmployees = employeeQueryResult.data || [];
  const translate = useTranslate();

  const employeesOfDepartment = departmentService.departments.find(
    (dep) => dep.id === department.id,
  )?.employees;
  const toast = useToast();

  const departmentContainsEmployees =
    employeesOfDepartment !== undefined && employeesOfDepartment.length !== 0;

  const toggleShowEdit = () => {
    setShowEdit(!showEdit);
  };

  type EditDepartmentValues = {
    name: string;
    description: string;
  };

  const onEditModalAction = (values: EditDepartmentValues) => {
    void departmentService.changeDepartment(
      department,
      values.name,
      values.description,
      toggleShowEdit,
    );
  };

  const toggleShowDelete = () => {
    setShowDelete(!showDelete);
  };

  const onDeleteModalAction = () => {
    toggleShowDelete();
    void departmentService.deleteDepartment(department);
  };

  const employeeWasAlreadyAdded = (employeeId: string): boolean => {
    if (department && department.employees && department.employees.length > 0) {
      return department.employees.find((e) => e.id == employeeId) !== undefined;
    }
    return false;
  };

  const addEmployeeToDepartment = (employeeId: string) => {
    if (
      employeeId !== '' &&
      !employeeWasAlreadyAdded(employeeId) &&
      employeeId !=
        translate('departmentTile.addEmployee.input.employeeId.placeholder')
    ) {
      const employee = allEmployees.find((e) => e.id == employeeId);
      if (employee) {
        void departmentService.addEmployeeToDepartment(employee, department);
      }
    } else {
      toast({
        title: translate('departmentTile.addEmployee.toast.error.title'),
        description: translate(
          'departmentTile.addEmployee.toast.error.description',
        ),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fillAllEmployesWhoAreNotAlreadyAssignedToADepartment = () => {
    const notAssignedEmployees = allEmployees.filter(
      (employee) =>
        employeesOfDepartment?.findIndex((e) => e.id === employee.id) === -1,
    );
    return notAssignedEmployees;
  };

  const allEmployesWhoAreNotAlreadyAssignedToADepartment =
    fillAllEmployesWhoAreNotAlreadyAssignedToADepartment();

  const departmentRef = useRef(null);

  const addAllEmployeesToDepartment = async () => {
    for (const employee of allEmployesWhoAreNotAlreadyAssignedToADepartment) {
      await departmentService.addEmployeeToDepartment(employee, department);
    }
  };

  useEffect(() => {
    if (openDepartment === department.id && departmentRef.current) {
      setTimeout(() => {
        const ref = departmentRef.current as HTMLDivElement | null;
        if (ref) {
          const windowHeight = window.innerHeight;
          const rect = ref.getBoundingClientRect();
          const elementTop = rect.top;
          if (elementTop > windowHeight / 2) {
            ref.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
      }, 100);
    }
  }, [openDepartment, department.id]);
  type FormValues = {
    employeeId: string;
  };

  return (
    <Formik
      initialValues={{
        employeeId:
          allEmployesWhoAreNotAlreadyAssignedToADepartment.length > 0
            ? allEmployesWhoAreNotAlreadyAssignedToADepartment[0]!.id
            : '',
      }}
      onSubmit={(
        values: FormValues,
        { setFieldError, setFieldValue }: FormikHelpers<FormValues>,
      ) => {
        if (values.employeeId == 'all') {
          setFieldError('employeeId', '');
          void addAllEmployeesToDepartment();
        } else {
          addEmployeeToDepartment(values.employeeId);
          setFieldValue('employeeId', 'all');
        }
      }}
    >
      {(formik) => (
        <Form>
          <DefaultScreenTileBox>
            <Grid templateColumns="repeat(6, 1fr)" columnGap={4}>
              <GridItem colSpan={2} mt={2}>
                <Text fontSize="lg">{department.name}</Text>
              </GridItem>
              <GridItem colSpan={2}>
                <LabeledText
                  label="departmentTile.description"
                  fontSize="md"
                  mb={3}
                >
                  {department.description}
                </LabeledText>
              </GridItem>
              <GridItem>
                <LabeledText label="department.employee.amount">
                  {department?.employees?.length ?? 0}
                </LabeledText>
              </GridItem>
              <GridItem>
                <Flex justify="flex-end" align="center">
                  <Tooltip
                    label={translate(
                      'departmentTile.departmentEdit.tooltip.label',
                    )}
                    aria-label={translate(
                      'departmentTile.departmentEdit.tooltip.label',
                    )}
                  >
                    <IconButton
                      icon={<EditIcon />}
                      onClick={toggleShowEdit}
                      aria-label={
                        translate(
                          'departmentTile.departmentEdit.tooltip.label',
                        ) || ''
                      }
                      isRound
                      mr={1}
                    />
                  </Tooltip>
                  <Tooltip
                    label={translate(
                      'departmentTile.departmentDelete.tooltip.label',
                    )}
                    aria-label={translate(
                      'departmentTile.departmentDelete.tooltip.label',
                    )}
                  >
                    <IconButton
                      icon={<DeleteIcon />}
                      onClick={toggleShowDelete}
                      aria-label={
                        translate(
                          'departmentTile.departmentDelete.tooltip.label',
                        ) || ''
                      }
                      isRound
                      mr={1}
                    />
                  </Tooltip>
                  <CollapseButton
                    isOpen={openDepartment === department.id}
                    toggleIsOpen={() => {
                      formik.setFieldValue('employeeId', 'all');
                      setOpenDepartment(
                        openDepartment === department.id
                          ? undefined
                          : department.id,
                      );
                    }}
                  />
                </Flex>
              </GridItem>
            </Grid>
            <DefaultModalDialog
              dialogHeaderText="departmentTile.departmentEdit.dialogHeaderText"
              submitText="departmentTile.departmentEdit.submitText"
              isOpen={showEdit}
              onCancel={toggleShowEdit}
              onSubmit={onEditModalAction}
              showCancelButton
              size="xl"
              initialValues={
                {
                  name: department.name,
                  description: department.description,
                } as EditDepartmentValues
              }
            >
              <Flex direction="column">
                <InputField
                  name="name"
                  label=""
                  placeholder="departmentTile.departmentEdit.input.name.placeholder"
                  mb={5}
                />
                <InputField
                  name="description"
                  label=""
                  placeholder="departmentTile.departmentEdit.input.description.placeholder"
                  textarea
                />
              </Flex>
            </DefaultModalDialog>
            <DefaultAlertDialog
              dialogHeaderText="departmentTile.departmentDelete.dialogHeaderText"
              dialogBodyText="departmentTile.departmentDelete.dialogBodyText"
              isOpen={showDelete}
              onConfirmedAction={onDeleteModalAction}
              onCancel={toggleShowDelete}
            />
            <Collapse in={openDepartment === department.id}>
              <div ref={departmentRef}>
                {departmentContainsEmployees ? (
                  <EmployeesInTile
                    employees={employeesOfDepartment}
                    department={department}
                    tooltipLabel="employeesInDepartmentTile.tooltip.label"
                    dialogHeaderText="employeesInDepartmentTile.delete.dialogHeaderText"
                    dialogBodyText="employeesInDepartmentTile.delete.dialogBodyText"
                  />
                ) : (
                  <Text fontSize="md">
                    departmentTile.showEmployees.noEmployees.text
                  </Text>
                )}
                <Flex mt={2}>
                  <Button type="submit" mr={5}>
                    departmentTile.addEmployee.button.text
                  </Button>
                  <SelectField
                    name="employeeId"
                    defaultvalue={true}
                    fields={[
                      {
                        id: 'all',
                        name: 'departmentTile.addEmployee.option.addAll',
                      },
                      ..._.orderBy(
                        allEmployesWhoAreNotAlreadyAssignedToADepartment,
                        [(e) => formatName(e)],
                        'asc',
                      ),
                    ]}
                    keys={(e) => e.id}
                    labels={(e) => {
                      if (e.id === 'all') {
                        return (e as { id: string; name: string }).name;
                      }
                      return formatName(e as Employee);
                    }}
                    values={(e) => e.id}
                    w="100%"
                  />
                </Flex>
              </div>
            </Collapse>
          </DefaultScreenTileBox>
        </Form>
      )}
    </Formik>
  );
};

export default DepartmentTile;
